import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Page from "../../layout/Page";
import ContentSection from "../../components/ContentSection";
import {
  useProductTypeByPath,
  useProductTypes
} from "../../hooks/SettingsHooks";
import Herobox from "../../components/Herobox";
import Breadcrumbs from "../../components/Breadcrumbs";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import {
  guidesBaseUrl,
  productTypeBaseUrl,
  supportBaseUrl
} from "../../config/urls";
import { tdsUrl } from "../../config/apiUrls";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import ProductSelect from "../../components/ProductSelect";
import {
  usePublicProduct,
  usePublicProductsByProductType
} from "../../hooks/ProductHooks";
import { AnimatePresence, motion } from "motion/react";
import GuideListing from "../../components/Guide/GuideListing";
import ProductDocuments from "../../components/ProductDocuments";
import ProductWell from "../../components/ProductWell";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import { uniqBy } from "lodash";
import { useGuides } from "../../hooks/CmsHooks";
import NotFoundPage from "../NotFoundPage";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import {
  productIsUnavailable,
  useProductSupportUrl
} from "../../utils/ProductUtils";
import { useUser } from "../../functional-components/UserContext";
import {
  cmsUrl,
  cockpitEditUrl,
  getHeroboxBackgroundImagePath
} from "../../utils/CmsUtils";
import { useLang } from "../../hooks/LangHooks";
import { unwrapAsset } from "../../utils/CockpitUtils";

import "./style.scss";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { getCurrentSite } from "../../utils/SiteUtils";
import { sites } from "../../config/sites";

const SimpleProductListItem = ({ product }) => {
  const to = useProductSupportUrl(product);
  return (
    <motion.li
      initial={{ opacity: 0, translateX: 10 }}
      animate={{ opacity: 1, translateX: 0 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.2,
        type: "spring",
        stiffness: 200
      }}
    >
      <Link to={to}>
        <Icon name={"cube"} /> <span className="text-pale">{product.sku} </span>
        {product.name}
      </Link>
    </motion.li>
  );
};

/** Filter products by name or SKU */
const filterProducts = (products, needle) => {
  if (!needle || !products) {
    return products;
  }

  const needleParts = needle.trim().toLowerCase().split(" ");
  return products.filter(product => {
    const productName = product.name.trim().toLowerCase().split(" ");
    const productSku = product.sku.trim().toLowerCase().split(" ");
    /** Partial match for all parts */
    return needleParts.every(part => {
      return (
        productName.some(namePart => namePart.startsWith(part)) ||
        productSku.some(skuPart => skuPart.startsWith(part))
      );
    });
  });
};

function productTypeNotHiddenOnCurrentSite(productType) {
  return (
    productType &&
    (productType.hide_on_sites || []).indexOf(getCurrentSite()) < 0
  );
}

const ProductSupportPage = () => {
  const lang = useLang();
  const navigate = useNavigate();
  const site = getCurrentSite();
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  const { admin } = useUser();
  const productTypes = useProductTypes();
  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  const features = useSiteFeatures();
  const guides = useGuides();
  const params = useParams();
  const productTypePath = params.product_type_path;
  const productType = useProductTypeByPath(productTypePath, guides);
  const _productTypeProducts = usePublicProductsByProductType(productType?._id);
  const productTypeProducts = useMemo(
    () =>
      (_productTypeProducts || []).sort((a, b) => a.name.localeCompare(b.name)),
    [_productTypeProducts]
  );

  console.log("product type:", productType);
  const artSlug = params.art_slug || "";
  const artId = artSlug.split("-")[0];
  const product = usePublicProduct(artId);
  console.log("product:", product, params);
  const [productFilter, setProductFilter] = useState("");

  useEffect(() => {
    registerCurrentPaths(
      productType?.paths
        ? Object.fromEntries(
            Object.entries(productType?.paths).map(([lang, path]) => [
              lang,
              supportBaseUrl +
                "/products/" +
                path +
                (artSlug ? "/" + artSlug : "")
            ])
          )
        : { all: supportBaseUrl + "/products" }
    );
    const listedOnSites = sites.filter(
      site => (productType?.hide_on_sites || []).indexOf(site) < 0
    );
    setAvailableOnSites(listedOnSites);
  }, [
    artSlug,
    registerCurrentPaths,
    setAvailableOnSites,
    productType?.paths,
    productType?.hide_on_sites
  ]);

  if (productTypePath && !productType && !product) {
    return <NotFoundPage />;
  }

  //The page is expected to have an herobox as the first component. This will not be displayed as page content.
  const heroBkg = getHeroboxBackgroundImagePath(productType?.description);

  const title = product
    ? t("support.for", {
        subject: productIsUnavailable(product)
          ? `${tp("shipping_status.unavailable").toUpperCase()} ${product.name}`
          : product.name
      })
    : productType
    ? t("support.for", { subject: productType.name })
    : t("support.products");

  const allDocuments = uniqBy(
    (product?.documents || []).concat(productType?.documents || []),
    "_id"
  );

  const filteredProducts = filterProducts(productTypeProducts, productFilter);

  const breadcrumbItems = product
    ? [
        { url: supportBaseUrl, text: t("support") },
        { url: supportBaseUrl + "/products", text: t("support.products") },
        {
          url: supportBaseUrl + "/products/" + productType?.path,
          text: productType?.name
        }
      ]
    : productType
    ? [
        { url: supportBaseUrl, text: t("support") },
        { url: supportBaseUrl + "/products", text: t("support.products") },
        {
          text: productType?.name
        }
      ]
    : [
        { url: supportBaseUrl, text: t("support") },
        { text: t("support.products") }
      ];

  return (
    <>
      <Helmet>
        <meta property="og:site_name" content={"Bjelin"} />
        <meta property="og:type" content={"website"} />
        <meta property="og:title" content={title} />
      </Helmet>

      <Page
        name="product-type-page"
        key={"product-types"}
        title={title}
        scrollToTop="smooth"
      >
        <Herobox
          type={(!heroBkg && productType) || product ? "medium" : "fat"}
          textShadow={true}
          className={"text-center"}
          backgroundImage={
            (heroBkg || productType) && !product
              ? unwrapAsset(productType.image)
              : product?.images?.[0]
              ? cmsUrl(product.images[0].path)
              : "https://cdn.bjelin.se/uploads/2021/04/29/Guide-herobox_uid_608a704384272.jpg"
          }
        >
          {!product && heroBkg ? (
            <CockpitLayoutComponents
              components={productType?.description[0].children}
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {!product && <h1>{title}</h1>}
              {(product || !productType) && (
                <>
                  <div style={{ margin: "2em 0 0.4em" }}>
                    {t("support.products.search")}
                  </div>
                  <ProductSelect
                    initialValue={product}
                    onChange={product => {
                      if (product) {
                        const productType = productTypes.find(
                          pt => pt._id === product.productType
                        );
                        const path = productType?.path || "uncategorized";
                        const slug = (product.name || "")
                          .trim()
                          .toLowerCase()
                          .replace(new RegExp("\\W+", "g"), "-");
                        const url = `${productTypeBaseUrl}/${path}/${product.sku}-${slug}`;
                        navigate(url, { replace: true });
                      } else {
                        navigate(productTypeBaseUrl, { replace: true });
                      }
                    }}
                  />
                  {!product && (
                    <div style={{ margin: "4em 0" }}>
                      {t("support.products.search.instructions")}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Herobox>

        <ContentSection containerTheme={"white"}>
          <Breadcrumbs items={breadcrumbItems} />
        </ContentSection>

        <AnimatePresence mode={"wait"} initial={false}>
          {product && !product.error ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ContentSection
                containerTheme={"white"}
                padded
                fadeIn
                navTitle={t("support.for", { subject: product.name })}
              >
                <h2>{t("support.for", { subject: product.name })}</h2>
                {features.guides() && <GuideListing tags={product.tags} />}
                <ProductDocuments documents={allDocuments} />
              </ContentSection>
              <ProductWell product={product} />
            </motion.div>
          ) : productType ? (
            <>
              {productType?.description && (
                <>
                  <CockpitLayoutComponents
                    components={productType?.description?.slice(
                      heroBkg ? 1 : 0
                    )}
                  />
                  {admin && (
                    <ContentSection containerTheme={"smoke"}>
                      <a
                        href={cockpitEditUrl(
                          "product_types",
                          productType._id,
                          lang
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("edit.product-type")} <Icon name="pen-to-square" />
                      </a>
                    </ContentSection>
                  )}
                </>
              )}
              {productType?.has_tds && (
                <ContentSection
                  containerTheme={"white"}
                  navTitle={t("document.technical-data-sheet")}
                  navID="tds"
                >
                  <h2>{t("document.technical-data-sheet")}</h2>
                  <a
                    href={tdsUrl(productType, site, lang)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name={"table"} /> {t("document.technical-data-sheet")}
                  </a>
                </ContentSection>
              )}
              {(features.guides() && productType.guides?.length > 0) ||
              productType.documents?.length > 0 ? (
                <>
                  {features.guides() && productType.guides?.length > 0 && (
                    <ContentSection
                      containerTheme={"white"}
                      fadeIn
                      navTitle={t("guides", { subject: productType.name })}
                      navID="guides"
                    >
                      <h2>{t("guides", { subject: productType.name })}</h2>
                      <ul className="guide-listing">
                        {productType.guides.map((guide, idx) => (
                          <li key={idx}>
                            <Link to={`${guidesBaseUrl}/${guide.path}`}>
                              <Icon name={"book-blank"} /> {guide.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </ContentSection>
                  )}
                  {productType.documents?.length > 0 && (
                    <ContentSection
                      containerTheme={"white"}
                      fadeIn
                      navTitle={t("documents", { subject: productType.name })}
                      navID="docs"
                    >
                      <h2>{t("documents", { subject: productType.name })}</h2>
                      <ProductDocuments documents={productType.documents} />
                    </ContentSection>
                  )}
                </>
              ) : (
                <p>{t("support.content.empty")}</p>
              )}
              {productTypeProducts.length > 0 && (
                <ContentSection
                  containerTheme={"white"}
                  fadeIn
                  navTitle={t("products.compatible")}
                  navID="products"
                >
                  <h2>{t("products.compatible")}</h2>
                  <div
                    className={`product-filter-field-container  ${
                      filteredProducts.length === 0 ? "empty" : ""
                    } ${
                      filteredProducts.length > 0 && productFilter !== ""
                        ? "active"
                        : ""
                    }`}
                  >
                    <Icon
                      name={
                        filteredProducts.length === 0 && productFilter !== ""
                          ? "filter-circle-xmark"
                          : "filter"
                      }
                      type={productFilter !== "" ? "fas" : "fal"}
                      className={"inner-icon"}
                    />
                    <input
                      name={"productFilter"}
                      type={"text"}
                      className={"product-filter-field"}
                      placeholder={t("products.filter.placeholder")}
                      value={productFilter}
                      onChange={e => setProductFilter(e.target.value)}
                    />
                  </div>
                  <ul
                    className={`product-listing ${
                      filteredProducts.length > 10 ? "multi-col" : ""
                    }`}
                  >
                    <AnimatePresence initial={false}>
                      {filteredProducts.map((prod, idx) => (
                        <SimpleProductListItem key={idx} product={prod} />
                      ))}
                    </AnimatePresence>
                  </ul>
                </ContentSection>
              )}
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ContentSection
                containerTheme={"white"}
                padded
                fadeIn
                navTitle={t("product-types")}
                navID="types"
              >
                <h2>{t("product-types")}</h2>
                {productTypes ? (
                  <ul className="guide-listing">
                    {productTypes
                      .filter(productTypeNotHiddenOnCurrentSite)
                      .map((pt, idx) => (
                        <li key={idx}>
                          <Link to={productTypeBaseUrl + "/" + pt.path}>
                            <Icon name={"book-blank"} /> {pt.name}
                          </Link>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p>{t("product-types.empty")}</p>
                )}
              </ContentSection>
            </motion.div>
          )}
        </AnimatePresence>
      </Page>
    </>
  );
};

export default ProductSupportPage;
